// Svg
import WaitSvg from '../assets/wait.svg';
import CompletedSvg from '../assets/completed.svg';
import MigrateSVG from '../assets/migrate.svg';
import ArchiveSvg from '@/assets/archive.svg';
import ShareSvg from '@/assets/share.svg';
import OpenSvg from '@/assets/open.svg';

// Other
import INumeric from '@/core/Models/INumeric';
import { computed, DefineComponent, h, isRef, markRaw, onBeforeUnmount, ref, Ref } from 'vue';
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { $confirm, $error } from '@/utils/app-utils';
import AuthorizationProvider from '@/core/Authorization/AuthorizationProvider';
import Operations from '@/core/Authorization/Operations';
import NumericService from '@/core/Services/NumericService';
import IPatchNumericRequest from '@/core/Requests/IPatchNumericRequest';
import NumericCompletionCondition from '@/core/Values/NumericCompletionCondition';
import QuarterNumber from '@/core/Values/QuarterNumber';
import MutationBus from '@/core/Mutations/MutationBus';
import { INumericsMutationContextOptions, NumericMutatorContext } from '@/core/Mutators/NumericMutator';
import store from '@/store';
import IQueryNumericRequest from '@/core/Requests/IQueryNumericRequest';
import Storages from '@/core/Storages';
import Settings from '@/core/Settings';
import VDatePicker from '@/components/VDatePicker.vue';
import { IDropdownOption } from '@/core/Values/IDropdownOption';
import { IFilterPopupOption, InitFilterPopupOption } from '@/core/Values/IFilterPopup';
import VUserSelectorNew from '@/components/VUserSelectorNew.vue';
import NumericViewMode from '@/core/Values/NumericViewMode';
import { copyTextToClipboard } from '@/utils/utils';
import ContextMenu, { MenuItem } from '@imengyu/vue3-context-menu';
import ConfirmContext from '@/core/Values/ConfirmContext';
import MutationType from '@/core/Mutations/MutationType';
import ObjectType from '@/core/Mutations/ObjectType';
import VCheckbox from '@/components/VCheckbox.vue';

interface INumericAction extends IDropdownOption {
    show: boolean;
    panelClasses?: string;
    isMainAction: boolean;
}

const api = {
    async patchAsync(numeric: INumeric, patch: IPatchNumericRequest) {
        try {
            await NumericService.patchAsync(numeric.id, patch);
        } catch (error) {
            $error(error);
        }
    },
    async archiveAsync(numeric: INumeric) {
        try {
            return await NumericService.archiveAsync(numeric.id);
        } catch (error) {
            $error(error);
        }
    },
    async migrateAsync(numeric: INumeric, targetYear: number = numeric.year + 1) {
        try {
            return await NumericService.migrateAsync({ targetId: numeric.id, targetYear });
        } catch (error) {
            $error(error);
        }
    },
};

function useUserFilter(title: string, key: keyof IQueryNumericRequest): InitFilterPopupOption {
    return (context) => {
        const filters = context.value as IQueryNumericRequest;
        const hasFilter = Object.prototype.hasOwnProperty.call(filters, key);
        const modelValue = filters[key];

        const attrs = {
            tiny: true,
            multiple: true,
            modelValue,
            class: 'v-select--primary v-select--visible v-select--no-option-padding',
        };

        const option = {
            title: context.t(title),
            action: () => {
                context.addFilter(option);
                context.removeOption(option);
            },
            on: {
                'update:modelValue': (value: string[]) => {
                    attrs.modelValue = value;
                    context.submit({ [key]: value });
                },
            },
            attrs,
            component: markRaw(VUserSelectorNew),
        };

        if (hasFilter) {
            context.addFilter(option);
        } else {
            context.addOption(option);
        }
    };
}

const filterOptionsNamed: Partial<Record<keyof IQueryNumericRequest, InitFilterPopupOption>> = {
    withArchived(context) {
        const filters = context.value;
        const modelValue = !!filters.withArchived;

        const attrs = {
            label: context.t('show-archived'),
            modelValue,
        };

        const option: IFilterPopupOption = {
            title: context.t('show-archived'),
            isCheckbox: true,
            on: {
                'update:modelValue': (val: boolean) => {
                    attrs.modelValue = val;
                    context.submit({ withArchived: val });
                },
            },
            attrs,
            component: markRaw(VCheckbox),
        };

        if (modelValue) {
            context.addFilter(option);
        } else {
            context.addOption(option);
        }
    },
    whereYear(context) {
        const filters = context.value as IQueryNumericRequest;
        const hasFilter = Object.prototype.hasOwnProperty.call(filters, 'whereYear');
        const modelValue = Array.isArray(filters.whereYear) ? filters.whereYear[0]?.toString() ?? '' : '';

        const attrs = {
            class: 'date-picker--outline',
            config: {
                yearPicker: true,
                autoApply: true,
                clearable: false,
                startDate: new Date(),
                minDate: new Date(new Date().getFullYear() - 5, 1),
                maxDate: new Date(new Date().getFullYear() + 5, 1),
                textInput: {
                    format: 'yyyy',
                },
            },
            modelValue,
        };

        const option: IDropdownOption & IFilterPopupOption = {
            title: context.t('year-full'),
            action: () => {
                context.addFilter(option);
                context.removeOption(option);
            },
            on: {
                'update:modelValue': (value: string) => {
                    attrs.modelValue = value.toString();
                    context.submit({ whereYear: [value] });
                },
            },
            attrs,
            component: markRaw(VDatePicker),
        };

        if (hasFilter) {
            context.addFilter(option);
        } else {
            context.addOption(option);
        }
    },
    whereAuthorId: useUserFilter('author', 'whereAuthorId'),
    whereAssigneeId: useUserFilter('assignee', 'whereAssigneeId'),
};
const filterOptions = Object.values(filterOptionsNamed);

export function useNumericForm(numeric: Ref<INumeric>) {
    const editable = computed(() => true);
    const disabled = computed(() => !!numeric.value.archivedAt);
    const isFullfiled = computed(() => numeric.value.name && numeric.value.assigneeId && numeric.value.counterId);

    type CounterPatch = { counterId: number; numericTagName: string; numericTagValue: string };
    type ExpectedValuePatch = Pick<
        IPatchNumericRequest,
        'expectedValueQ1' | 'expectedValueQ2' | 'expectedValueQ3' | 'expectedValueQ4'
    >;

    return {
        editable,
        disabled,
        isFullfiled,

        commitAsync: (quarter: QuarterNumber) => api.patchAsync(numeric.value, { commit: quarter }),
        setYearAsync: (year: number) => api.patchAsync(numeric.value, { year }),
        setNameAsync: (name: string) => api.patchAsync(numeric.value, { name }),
        setRemarkAsync: (remark: string) => api.patchAsync(numeric.value, { remark }),
        setCommentAsync: (comment: string) => api.patchAsync(numeric.value, { comment }),
        setTaskAsync: (taskId: number) => api.patchAsync(numeric.value, { taskId }),
        setAuthorAsync: (authorId: string) => api.patchAsync(numeric.value, { authorId }),
        setAssignedAsync: (assigneeId: string) => api.patchAsync(numeric.value, { assigneeId: assigneeId }),
        setCounterAsync: (counter: CounterPatch) =>
            api.patchAsync(numeric.value, {
                counterId: counter.counterId,
                counterTagName: counter.numericTagName,
                counterTagValue: counter.numericTagValue,
            }),
        setCounterDivisorAsync: (counterDivisor: CounterPatch) =>
            api.patchAsync(numeric.value, {
                counterDivisorId: counterDivisor.counterId,
                counterDivisorTagName: counterDivisor.numericTagName,
                counterDivisorTagValue: counterDivisor.numericTagValue,
            }),
        setExpectedValueAsync: (patch: ExpectedValuePatch) => api.patchAsync(numeric.value, patch),
        setConditionAsync: (condition: NumericCompletionCondition) => api.patchAsync(numeric.value, { condition }),
        setViewModeAsync: (viewMode: NumericViewMode) => api.patchAsync(numeric.value, { viewMode }),
    };
}

export function useNumericMutator(
    options: Partial<INumericsMutationContextOptions> | null = null,
    filters: Ref<IQueryNumericRequest> | null = null,
) {
    const values = ref([] as INumeric[]);
    const currentUser = store.state.user;

    options = options ?? {};

    if (filters && !options.fetchNumeric) {
        // Fetches a task if the patch contains the changes that can affect of the filters.
        options.fetchNumeric = async (patch: Partial<INumeric>) => {
            if (!NumericService.matchPatch(patch, currentUser?.id as string, filters.value)) {
                return undefined;
            }

            const numerics = await NumericService.queryAsync({
                ...filters?.value,
                whereId: [patch.id as number],
                perPage: 1,
            });

            return numerics[0];
        };
    }

    if (filters && !options.excludeNumeric) {
        // Excludes numeric if they are not matching the specified filters.
        options.excludeNumeric = (numeric: INumeric) =>
            !NumericService.match(numeric, currentUser?.id as string, filters.value);
    }

    if (filters && !options.excludeNumeric) {
        // Ignores numeric creation if they are not matching the specified filters.
        options.excludeNumeric = (numeric: INumeric) =>
            NumericService.match(numeric, currentUser?.id as string, filters.value);
    }

    const mutatorContext = new NumericMutatorContext(values, options);

    onBeforeUnmount(() => MutationBus.deactivate(mutatorContext));

    function trackMutations(numerics: INumeric | INumeric[]) {
        if (!Array.isArray(numerics)) {
            numerics = [numerics];
        }

        values.value = numerics;

        MutationBus.activate(mutatorContext);
    }

    return { trackMutations };
}

export function useNumericFilters(
    name: string,
    customFilters: IQueryNumericRequest = {},
    staticFilters: Ref<IQueryNumericRequest> | IQueryNumericRequest | null = null,
) {
    const storeFilters = computed(Storages.Filters.computed(Settings.UI.Filters + '.' + name, customFilters));
    const searchString = ref('');

    const filters = computed<IQueryNumericRequest>(() => ({
        ...storeFilters.value,
        ...(isRef(staticFilters) ? staticFilters.value : staticFilters),
        search: searchString.value,
    }));

    return { searchString, filters, filterOptions, filterOptionsNamed, customFilters: storeFilters };
}

export function useNumericPermissions(numeric: Ref<INumeric>) {
    const canRead = AuthorizationProvider.authorize(numeric.value, Operations.Numerics.Read);
    const canCreate = AuthorizationProvider.authorize(numeric.value, Operations.Numerics.Create);
    const canDelete = AuthorizationProvider.authorize(numeric.value, Operations.Numerics.Delete);
    const canUpdatePublicFields = AuthorizationProvider.authorize(
        numeric.value,
        Operations.Numerics.UpdatePublicFields,
    );
    const canUpdatePrivateFields = AuthorizationProvider.authorize(
        numeric.value,
        Operations.Numerics.UpdatePrivateFields,
    );

    return {
        canRead,
        canCreate,
        canDelete,
        canUpdatePublicFields,
        canUpdatePrivateFields,
    };
}

export function useNumericCard(goal: Ref<INumeric>) {
    const route = useRoute();
    const isSelected = computed(() => goal.value.id === parseInt(route.query.numeric as string, 10));

    return { isSelected };
}

export interface INumericCurrentState {
    icon: DefineComponent | null;
    success: boolean;
    waiting: boolean;
    hasCommitedQuarters: boolean;
}

export interface INumericQuarterCompletion {
    icon: DefineComponent | null;
    success: boolean;
    warning: boolean;
    negative: boolean;
    waiting: boolean;
    title: string;
    value: string;
}

export interface INumericCompletion {
    current: INumericCurrentState;
    quarter1: INumericQuarterCompletion;
    quarter2: INumericQuarterCompletion;
    quarter3: INumericQuarterCompletion;
    quarter4: INumericQuarterCompletion;
}

export function useNumericCompletion(goal: Ref<INumeric>) {
    const { t } = useI18n();

    const completion = computed(
        (): INumericCompletion => ({
            current: getCurrentState(goal.value),
            quarter1: getQuarterCompletion(goal.value, 1),
            quarter2: getQuarterCompletion(goal.value, 2),
            quarter3: getQuarterCompletion(goal.value, 3),
            quarter4: getQuarterCompletion(goal.value, 4),
        }),
    );

    function getCurrentState(goal: INumeric): INumericCurrentState {
        const currentQuarter = Math.floor((new Date().getMonth() + 3) / 3);

        const isCommitted = !!(goal.committedAtQ1 && goal.committedAtQ2 && goal.committedAtQ3 && goal.committedAtQ4);
        const isWaitingForCommit =
            (currentQuarter >= 1 && !goal.committedAtQ1) ||
            (currentQuarter >= 2 && !goal.committedAtQ2) ||
            (currentQuarter >= 3 && !goal.committedAtQ3) ||
            (currentQuarter >= 4 && !goal.committedAtQ4);

        const hasCommitedQuarters =
            !!goal.committedAtQ1 || !!goal.committedAtQ2 || !!goal.committedAtQ3 || !!goal.committedAtQ4;

        return {
            icon: isWaitingForCommit ? WaitSvg : CompletedSvg,
            success: isCommitted,
            waiting: isWaitingForCommit,
            hasCommitedQuarters: hasCommitedQuarters,
        };
    }

    function getQuarterCompletion(goal: INumeric, quarter: number): INumericQuarterCompletion {
        const date = new Date();
        const currentYear = date.getFullYear();
        const currentQuarter = Math.floor((date.getMonth() + 3) / 3);

        const committedAt =
            quarter === 1
                ? goal.committedAtQ1
                : quarter === 2
                ? goal.committedAtQ2
                : quarter === 3
                ? goal.committedAtQ3
                : quarter === 4
                ? goal.committedAtQ4
                : null;

        const completionRate =
            quarter === 1
                ? goal.completionRateQ1
                : quarter === 2
                ? goal.completionRateQ2
                : quarter === 3
                ? goal.completionRateQ3
                : quarter === 4
                ? goal.completionRateQ4
                : 0;

        const isCommitted = !!committedAt;
        const isWaitingForCommit =
            (goal.year < currentYear && !isCommitted) ||
            (goal.year === currentYear && quarter <= currentQuarter && !isCommitted);
        const isOverfulfillment = completionRate >= 100;
        const isNotCommittedInDeadline = quarter < currentQuarter && !isCommitted;

        return {
            icon: isWaitingForCommit ? WaitSvg : isCommitted ? CompletedSvg : null,
            success: isOverfulfillment,
            warning: isWaitingForCommit,
            negative: isNotCommittedInDeadline || (isCommitted && !isOverfulfillment),
            waiting: isWaitingForCommit,
            title: t('quarter' + quarter),
            value: Math.round(completionRate) + '%',
        };
    }

    return { completion };
}

export function useNumericContextMenu() {
    const { t } = useI18n();
    const router = useRouter();

    function getTaskActionsImpl(numeric: INumeric) {
        const actions: INumericAction[][] = [
            [
                {
                    show: true,
                    icon: markRaw(ShareSvg),
                    title: t('copy-link'),
                    action: () => {
                        const uri = window.location.origin + router.resolve(getNumericRouteLocation(numeric)).fullPath;
                        copyTextToClipboard(uri);
                    },
                    panelClasses: 'button--secondary button--green',
                    isMainAction: false,
                },
            ],
            [
                {
                    show: numeric.year + 1 <= new Date().getFullYear() + 5,
                    icon: markRaw(MigrateSVG),
                    title: t('confirm-numeric-migrate'),
                    action: () => {
                        migrateNumericWithConfirm(
                            numeric,
                            t('confirm-numeric-migrate-hint', { year: numeric.year + 1 }),
                        );
                    },
                    panelClasses: 'button--secondary button--green',
                    isMainAction: false,
                },
            ],
            [
                {
                    show: !numeric.archivedAt && AuthorizationProvider.authorize(numeric, Operations.Numerics.Archive),
                    icon: markRaw(ArchiveSvg),
                    title: t('archive'),
                    action: () =>
                        confirmSimpleAction(
                            t(`confirm-numeric-archiving`),
                            () => api.archiveAsync(numeric),
                            t(`confirm-numeric-archiving-hint`),
                        ),
                    classes: 'button--transparent-negative',
                    panelClasses: 'button--secondary button--negative',
                    isMainAction: false,
                },
            ],
        ];

        return actions.reduce((carry, actions) => {
            actions = actions.filter((action) => action.show);

            if (!carry.length) {
                return actions;
            }

            if (actions.length) {
                carry = [
                    ...carry,
                    {
                        show: true,
                        title: '',
                        action: () => 0,
                        classes: 'button__dropdown-option--separator',
                        isMainAction: false,
                    },
                    ...actions,
                ];
            }

            return carry;
        }, [] as INumericAction[]);
    }

    function getNumericActions(numeric: INumeric) {
        return getTaskActionsImpl(numeric);
    }

    function openContextMenu(event: MouseEvent, numeric: INumeric) {
        const actions = getNumericActions(numeric);
        openContextMenuImpl(event, numeric, actions);
    }

    function openContextMenuImpl(event: MouseEvent, numeric: INumeric, actions: INumericAction[]): void {
        //prevent the browser's default menu
        event.preventDefault();

        const defaultAction: MenuItem[] = [
            {
                icon: h(OpenSvg),
                label: t('open'),
                onClick: () => {
                    const route = getNumericRouteLocation(numeric);
                    router.push(route);
                },
            },
            {
                icon: h(OpenSvg),
                label: t('open-in-new-tab'),
                onClick: () => {
                    const uri = window.location.origin + router.resolve(getNumericRouteLocation(numeric)).fullPath;

                    window.open(uri, '_blank')?.focus();
                },
            },
            {
                divided: 'self',
            },
        ];

        ContextMenu.showContextMenu({
            x: event.x,
            y: event.y,
            zIndex: 100000,
            customClass: 'prevent-close',

            items: defaultAction.concat(
                actions.map((option): MenuItem => {
                    if (option.classes && option.classes.indexOf('button__dropdown-option--separator') !== -1) {
                        return {
                            divided: 'self',
                        };
                    }

                    return {
                        icon: option.icon ? h(option.icon) : undefined,
                        label: option.title,
                        customClass: option.classes,
                        onClick: (event) => option.action?.(option, event),
                    };
                }),
            ),
        });
    }

    function confirmSimpleAction(
        title: string,
        action: () => void,
        message = '',
        options: Partial<ConfirmContext> | null = null,
    ): void {
        $confirm({
            title,
            message,
            buttonOkName: t('yes'),
            okCallback: action,
            ...options,
        });
    }

    function migrateNumericWithConfirm(numeric: INumeric, hint?: string) {
        $confirm({
            message: hint ?? t('confirm-numeric-migrate-hint-2'),
            title: `${t('confirm-numeric-migrate')}?`,
            buttonOkClasses: 'button--green',
            buttonCancelClasses: 'button--negative',
            okCallback: async () => {
                const targetYear = numeric.year + 1 <= new Date().getFullYear() + 5 ? numeric.year + 1 : numeric.year;
                const mutations = await api.migrateAsync(numeric, targetYear);

                const mutation = mutations?.find(
                    (mutation) => mutation.type === MutationType.Created && mutation.objectType === ObjectType.Numeric,
                );

                if (!mutation) {
                    return;
                }

                const route = getNumericRouteLocation(mutation.objectState as INumeric);
                router.push(route);
            },
        });
    }

    return {
        migrateNumericWithConfirm,
        openContextMenu,
    };
}

export function getNumericRouteLocation(numeric: INumeric): RouteLocationRaw {
    return {
        query: {
            numeric: numeric.id,
        },
    };
}
